import { ExtendCurrency } from 'src/enums'
import validateCurrencyCode from 'validate-currency-code'

export interface CurrencyFormatterArgs {
  amount: number
  currency?: ExtendCurrency
  maximumFractionDigits?: number
  notation?: 'standard' | 'compact'
  forceHideCurrency?: boolean
  forceShowCurrency?: boolean
}

export type CurrencyFormatter = (args: CurrencyFormatterArgs) => string

export interface CurrencyCodeFormatPart {
  type: 'currencyCode'
  value: string
}

export type CurrencyFormatterPart =
  | Intl.NumberFormatPart
  | CurrencyCodeFormatPart

export type CurrencyPartsFormatter = (
  args: CurrencyFormatterArgs,
) => CurrencyFormatterPart[]

export function createCurrencyPartsFormatter(
  locale: string,
  defaultDisplayCurrency: ExtendCurrency,
): CurrencyPartsFormatter {
  return (args: CurrencyFormatterArgs): CurrencyFormatterPart[] => {
    const {
      amount,
      currency = defaultDisplayCurrency,
      maximumFractionDigits = 2,
      notation,
      forceHideCurrency = false,
      forceShowCurrency = false,
    } = args

    const validCurrency = validateCurrencyCode(currency)
      ? currency
      : defaultDisplayCurrency
    const parts: CurrencyFormatterPart[] = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: validCurrency,
      maximumFractionDigits,
      minimumFractionDigits: maximumFractionDigits,
      notation,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(amount)

    if (
      forceShowCurrency ||
      (!forceHideCurrency && defaultDisplayCurrency !== validCurrency)
    ) {
      parts.push(
        { type: 'literal', value: ' ' },
        { type: 'currencyCode', value: validCurrency },
      )
    }

    return parts
  }
}

export function createCurrencyFormatter(
  locale: string,
  defaultDisplayCurrency: ExtendCurrency,
): CurrencyFormatter {
  const formatCurrencyParts = createCurrencyPartsFormatter(
    locale,
    defaultDisplayCurrency,
  )

  return (args: CurrencyFormatterArgs): string =>
    formatCurrencyParts(args)
      .map(({ value }) => value)
      .join('')
}
